import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

// Material UI components
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Breadcrumbs,
  Link,
  TextField
} from '@mui/material';

// Material UI icons
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warehouse as WarehouseIcon,
  LocationOn as LocationOnIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon
} from '@mui/icons-material';

// Services
import { getWarehouses, deleteWarehouse } from '../../services/warehouseService';
import { getLocations } from '../../services/locationService';

const WarehouseList = () => {
  const navigate = useNavigate();
  
  // State
  const [warehouses, setWarehouses] = useState([]);
  const [locationCounts, setLocationCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [warehouseToDelete, setWarehouseToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Fetch warehouses on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch warehouses
        const warehousesData = await getWarehouses();
        setWarehouses(warehousesData);
        
        // Get location counts for each warehouse
        const counts = {};
        const locationsData = await getLocations();
        
        for (const warehouse of warehousesData) {
          counts[warehouse.id] = locationsData.filter(
            location => location.warehouse_id === warehouse.id
          ).length;
        }
        
        setLocationCounts(counts);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching warehouses:', err);
        setError('Failed to load warehouses. Please try again.');
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Handle delete warehouse
  const handleDeleteClick = (warehouse) => {
    setWarehouseToDelete(warehouse);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    try {
      await deleteWarehouse(warehouseToDelete.id);
      setWarehouses(warehouses.filter(w => w.id !== warehouseToDelete.id));
      setSnackbar({
        open: true,
        message: 'Warehouse deleted successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error deleting warehouse:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to delete warehouse',
        severity: 'error'
      });
    } finally {
      setDeleteDialogOpen(false);
      setWarehouseToDelete(null);
    }
  };
  
  // Handle search
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Filter warehouses based on search term
  const filteredWarehouses = warehouses.filter(warehouse => 
    warehouse.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    warehouse.location.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link 
          color="inherit" 
          href="#" 
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
        >
          Dashboard
        </Link>
        <Link 
          color="inherit" 
          href="#" 
          onClick={(e) => {
            e.preventDefault();
            navigate('/locations');
          }}
        >
          Locations
        </Link>
        <Typography color="text.primary">
          Warehouse Management
        </Typography>
      </Breadcrumbs>
      
      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <WarehouseIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Warehouse Management
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          color="primary" 
          component={RouterLink}
          to="/warehouses/add"
          startIcon={<AddIcon />}
        >
          Add New Warehouse
        </Button>
      </Box>
      
      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Search box */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search warehouses by name or location..."
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </Box>
      </Paper>
      
      {/* No warehouses message */}
      {warehouses.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No warehouses found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add your first warehouse to get started.
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            component={RouterLink}
            to="/warehouses/add"
            startIcon={<AddIcon />}
          >
            Add New Warehouse
          </Button>
        </Paper>
      )}
      
      {/* Warehouses list */}
      {warehouses.length > 0 && (
        <>
          {filteredWarehouses.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No warehouses match your search
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try a different search term or clear the search.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Warehouse Name</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell align="center">Locations</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredWarehouses.map((warehouse) => (
                    <TableRow key={warehouse.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <WarehouseIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body1" fontWeight="medium">
                            {warehouse.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{warehouse.location}</TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={`${locationCounts[warehouse.id] || 0} locations`} 
                          color={locationCounts[warehouse.id] > 0 ? 'primary' : 'default'}
                          variant={locationCounts[warehouse.id] > 0 ? 'filled' : 'outlined'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Tooltip title="View Locations">
                            <IconButton
                              color="primary"
                              onClick={() => navigate(`/locations?warehouse_id=${warehouse.id}`)}
                            >
                              <LocationOnIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Warehouse">
                            <IconButton
                              color="primary"
                              onClick={() => navigate(`/warehouses/edit/${warehouse.id}`)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Warehouse">
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteClick(warehouse)}
                              disabled={locationCounts[warehouse.id] > 0}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
      
      {/* Back button */}
      <Box sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/locations')}
        >
          Back to Locations
        </Button>
      </Box>
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Warehouse</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the warehouse "{warehouseToDelete?.name}"?
            {locationCounts[warehouseToDelete?.id] > 0 && (
              <Typography color="error" sx={{ mt: 2 }}>
                This warehouse has {locationCounts[warehouseToDelete?.id]} locations. You must delete or move these locations before deleting the warehouse.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={locationCounts[warehouseToDelete?.id] > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WarehouseList;